import { ref } from 'vue';

import { getMainPageBlocks, type MainPageBlock, type MainHeroItem } from '@/shared/api/generated/api';
import { recursiveToCamel } from '@/shared/helpers/toCamel';
import type { GuideSlide } from '@/shared/models/guide';
import type { Article } from '@/shared/models/journal';
import type { SeasonSlide } from '@/shared/models/season';

interface MainPageBlockDefaults {
  guidesTitle: string;
  guidesSubtitle: string;
  articlesTitle: string;
  articlesSubtitle: string;
}

// Загружает блоки главной страницы и раскладывает их по типизированным
// коллекциям (гиды/статьи/сезоны/hero). Заголовки берутся из блока, если он
// их присылает, иначе — из переданных значений по умолчанию.
export const useMainPageBlocks = (defaults: MainPageBlockDefaults) => {
  const guidesItems = ref<GuideSlide[]>([]);
  const guidesTitle = ref(defaults.guidesTitle);
  const guidesSubtitle = ref(defaults.guidesSubtitle);

  const articlesItems = ref<Article[]>([]);
  const articlesTitle = ref(defaults.articlesTitle);
  const articlesSubtitle = ref(defaults.articlesSubtitle);

  const seasonItems = ref<SeasonSlide[]>([]);
  const seasonTitle = ref('');
  const seasonSubtitle = ref('');
  const seasonColor = ref('');

  const heroItems = ref<MainHeroItem[]>([]);
  const heroTitle = ref<string | null>(null);

  const isDataLoaded = ref(false);

  const processGuidesBlock = (block: MainPageBlock) => {
    guidesItems.value = recursiveToCamel<GuideSlide[]>(block.items || []);
    if (block.title) guidesTitle.value = block.title;
    if (block.subtitle) guidesSubtitle.value = block.subtitle;
  };

  const processArticlesBlock = (block: MainPageBlock) => {
    articlesItems.value = recursiveToCamel<Article[]>(block.items || []);
    if (block.title) articlesTitle.value = block.title;
    if (block.subtitle) articlesSubtitle.value = block.subtitle;
  };

  const processSeasonsBlock = (block: MainPageBlock) => {
    seasonItems.value = recursiveToCamel<SeasonSlide[]>(block.items || []);
    if (block.title) seasonTitle.value = block.title;
    if (block.subtitle) seasonSubtitle.value = block.subtitle;
    if (block.extra?.color) seasonColor.value = block.extra.color as string;
  };

  const processMainHeroBlock = (block: MainPageBlock) => {
    heroItems.value = (block.items || []) as MainHeroItem[];
  };

  const processBlock = (block: MainPageBlock) => {
    switch (block.type) {
      case 'guides':
        processGuidesBlock(block);
        break;
      case 'articles':
        processArticlesBlock(block);
        break;
      case 'seasons':
        processSeasonsBlock(block);
        break;
      case 'main_hero':
        processMainHeroBlock(block);
        break;
      default:
        break;
    }
  };

  const fetchBlocks = async () => {
    try {
      const { data } = await getMainPageBlocks();
      if (data?.blocks) {
        data.blocks.forEach(processBlock);
      }
      isDataLoaded.value = true;
    } catch (e) {
      console.error('Failed to load main page blocks:', e);
    }
  };

  return {
    guidesItems,
    guidesTitle,
    guidesSubtitle,
    articlesItems,
    articlesTitle,
    articlesSubtitle,
    seasonItems,
    seasonTitle,
    seasonSubtitle,
    seasonColor,
    heroItems,
    heroTitle,
    isDataLoaded,
    fetchBlocks,
  };
};
